// MO4.1.2
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import Flex from '@wix/da-ds/pkg/Flex';
import { Url } from '@wix/da-url';
import BoostFilledIcon from '@wix/da-ds/pkg/Icons/16x16/BoostFilled';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import CheckIcon from '@wix/da-ds/pkg/Icons/16x16/Check';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import DiscountLabel from '../../DiscountLabel';
import Avatar from '../../User/Avatar';
import { getDeviationEntityId } from '../../redux/normalizr/schemas/deviation';
import BasicUserName from '../../User/BasicUserName';
import SubscriberStat from '../../Stats/SubscriberStat';
import useDeviationStats from '../../Stats/useDeviationStats';
import TierPriceWithBadge from '../Helper/TierPriceWithBadge';
import MinimalDumbo from '../Dumbo/MinimalDumbo';
import { getTierDescription, isBoostedDeviation } from '../_helpers';
import { useDeviationMatureFilter } from '../../utils/hooks/useDeviationMatureFilter';
import HiddenOverlay from '../HiddenOverlay';

import s from './TierSidebarCard.scss';

export interface Props {
  deviation: PapiDeviation;
  biData?: BiEvent;
  className?: string;
  showRecommendedBadge?: boolean;
  editTier?: () => void;
}

const TierSidebarCard: React.FC<Props> = ({
  deviation,
  biData,
  className,
  showRecommendedBadge = true,
  editTier,
}) => {
  const { t } = useTranslation();
  const showSaleIndicator = !!deviation?.tier?.discount;
  const { deviationId, author, title } = deviation;
  const { dollarPrice, isUserSubscribed } = deviation.tier || {};
  const { subscribers, isSubscribable, isInSubscription } =
    useDeviationStats(deviation);
  const isTierRelated = isSubscribable || isInSubscription;
  const isBoosted = isBoostedDeviation(deviation);

  const { showBlocked, overrideMatureFilter } =
    useDeviationMatureFilter(deviation);
  const [matureFilterOverridden, setMatureFilterOverridden] = useState(false);
  const onOverrideFilterClick = useCallback(
    e => {
      overrideMatureFilter();
      setMatureFilterOverridden(true);
      e.stopPropagation();
      e.preventDefault();
    },
    [overrideMatureFilter, setMatureFilterOverridden]
  );

  const cover_attachment = useFlag('cover_attachment');
  const coverImage = cover_attachment ? deviation : deviation.coverImage;

  if (!author?.username || !dollarPrice) {
    return null;
  }

  const tierEntityId = getDeviationEntityId(deviation);

  const coverWidth = 274;
  const coverHeight = 152;
  const href = isUserSubscribed
    ? Url.userSubscriptionsPath({
        username: author.username,
        tierDeviationId: deviationId,
        tierContentType: 'deviations',
        anchor: true,
      })
    : Url.userSubscriptionsDeeplink({
        username: author.username,
        tierEntityId,
      });

  const description = getTierDescription(deviation);

  return (
    <PlainButtonOrLink
      className={classnames(
        s['root'],
        isTierRelated && subscribers === 0 && s['no-subs'],
        className
      )}
      biData={biData}
      href={href}
    >
      {showBlocked && (
        <HiddenOverlay
          className={s['card-overlay']}
          showBackground={false}
          deviation={deviation}
          overrideMatureFilter={onOverrideFilterClick}
        />
      )}
      {/* use a non-linked version of UserIdentity here to prevent link nesting */}
      <div className={s['top-section']}>
        <div className={s['user-info']}>
          <Avatar user={author} size={24} withLink={false} />
          <BasicUserName
            usernameClassName={s['username']}
            symbolClassName={s['user-symbol']}
            user={author}
          />
          {isUserSubscribed && (
            <span className={s['subscribed']}>
              <CheckIcon
                className={s['subscribed-icon']}
                size={IconSize.SMALLER}
              />{' '}
              {t('deviation.tier.subscribed')}
            </span>
          )}
        </div>
      </div>

      <div className={s['cover-wrapper']}>
        {coverImage ? (
          <MinimalDumbo
            className={s['cover']}
            deviation={coverImage}
            height={coverHeight}
            width={coverWidth}
            showMatureContent={showBlocked || matureFilterOverridden}
          />
        ) : (
          <div
            className={classnames(s['cover'], s['placeholder-cover'])}
            style={{ height: coverHeight }}
          />
        )}
      </div>
      {title && <Heading className={s['title']}>{title}</Heading>}
      <Flex
        className={s['stats-bar']}
        direction="row"
        alignItems="center"
        gap={16}
      >
        <SubscriberStat className={s['subscribers']} deviation={deviation} />
        {isBoosted && (
          <div className={s['boosted']}>
            <BoostFilledIcon size={IconSize.SMALLER} disableDefaultColors />
            <span className={s['boosted-text']}>{t('common.boosted')}</span>
          </div>
        )}
      </Flex>
      {description && <div className={s['description']}>{description}</div>}

      <Flex alignItems="center" justifyContent="space-between">
        <TierPriceWithBadge className={s['price']} price={dollarPrice} />
        {showSaleIndicator && (
          <DiscountLabel
            className={s['sale-indicator']}
            textVariant="sale"
            discount={deviation?.tier?.discount}
          />
        )}
      </Flex>
    </PlainButtonOrLink>
  );
};
TierSidebarCard.displayName = 'TierSidebarCard';
export default TierSidebarCard;
